import { ReloadOutlined } from "@ant-design/icons"
import { Alert, App, Button, Form, Skeleton } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Navigate, useNavigate, useParams } from "react-router-dom"

import type { RichTextEditorHandle } from "../../components/RichTextEditor/RichTextEditor.tsx"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { unwrapEnvelope } from "../../utils/api/unwrap-envelope.ts"
import {
  createAutomation,
  disableAutomation,
  duplicateAutomation,
  enableAutomation,
  getAutomation,
  getAutomationOptions,
  getAutomations,
  updateAutomation
} from "./api/services.ts"
import { AutomationBuilder } from "./components/AutomationBuilder"
import { AutomationSurface } from "./components/AutomationSurface"
import {
  ActivationDialog,
  REVIEW_DIALOG_CLASS
} from "./components/builder/ActivationDialog"
import { BuilderHeader } from "./components/builder/BuilderHeader"
import { LIVE_REVIEW_ID, ReviewPanel } from "./components/ReviewPanel"
import {
  isConflict,
  isEnableBlocked,
  readErrorDetails
} from "./helpers/api-errors"
import { listStatus } from "./helpers/automation-list"
import { copyName } from "./helpers/copy-name"
import {
  focusField,
  focusFirstInvalid,
  isFieldShown,
  validateBuilder
} from "./helpers/focus-first-invalid"
import { scrollToAndFocus } from "./helpers/scroll-focus"
import {
  journeySteps,
  scheduleWarningText,
  summarizeFormValues
} from "./helpers/form-summary"
import {
  type AutomationFormValues,
  defaultFormValues,
  formValuesToPayload,
  recordToFormValues,
  scheduleWarning
} from "./helpers/form-values"
import { useUnsavedChangesGuard } from "./hooks/use-unsaved-changes-guard"
import type {
  Automation,
  AutomationOptions,
  AutomationPayload
} from "./types/automation.types"
import type { BuilderAction } from "./types/builder-action"

// The form edits timing as presets and parts; the API reports errors on the
// wire fields. Each goes on the preset, which is always on screen.
const WIRE_TIMING_FIELDS: Partial<
  Record<keyof AutomationFormValues, string[]>
> = {
  delay_preset: ["initial_delay_value", "initial_delay_unit"],
  cadence_preset: ["cadence_value", "cadence_unit"]
}

type ReviewMode = "activate" | "startNurturing"

// Enough to hold every automation a team has, for picking a free copy name.
const NAMES_PAGE_SIZE = 1000

// A saved record carries `disabled_reasons` only on the PUT that produced it.
const withoutSaveExtras = ({
  disabled_reasons: _reasons,
  ...record
}: Automation): Automation => record

function ReasonList({ reasons }: { reasons: string[] }) {
  return (
    <ul className={"m-0 list-disc pl-4"}>
      {reasons.map((reason) => (
        <li key={reason}>{reason}</li>
      ))}
    </ul>
  )
}

export default function AutomationBuilderPage() {
  const canManage = useAppSelector(
    (state) =>
      !!state.auth.permissions?.codes?.settings?.automations
        ?.settings_automation_manage
  )
  // The builder is for changing automations; a viewer has the list's History.
  if (!canManage)
    return <Navigate replace to={RoutesPaths.settings.automation} />
  return <AutomationBuilderScreen />
}

function AutomationBuilderScreen() {
  const { t } = useTranslation()
  const saveFailed = t("settingsAutomation.builder.saveFailed")
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const { message, modal } = App.useApp()

  const [form] = Form.useForm<AutomationFormValues>()
  const editorRef = useRef<RichTextEditorHandle>(null)

  const [options, setOptions] = useState<AutomationOptions | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [values, setValues] = useState<AutomationFormValues | null>(null)
  const [editing, setEditing] = useState<Automation | null>(null)
  const [busy, setBusy] = useState<BuilderAction | null>(null)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [reviewMode, setReviewMode] = useState<ReviewMode>("activate")
  const [saveWarnings, setSaveWarnings] = useState<string[]>([])
  const [activationBlocked, setActivationBlocked] = useState<string[]>([])
  const [activationBlockedPaused, setActivationBlockedPaused] = useState(false)
  const [announcement, setAnnouncement] = useState({ id: 0, text: "" })

  // Read inside effects, after awaits and by the navigation guard, where the
  // render's own copy is stale.
  const editingRef = useRef<Automation | null>(null)
  const requestedIdRef = useRef<string | undefined>(undefined)
  const dirtyRef = useRef(false)
  const busyRef = useRef<BuilderAction | null>(null)

  // Set while the page moves its own address after a save, which the guard
  // must let through even though that save still holds the lock.
  const ownNavigationRef = useRef(false)
  useUnsavedChangesGuard({ dirtyRef, busyRef, ownNavigationRef })

  // A save that finishes after the page has gone must not move the address.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const focusBody = useCallback(() => editorRef.current?.focus(), [])

  const refocusOnSwapRef = useRef(false)
  // Focus left on the page body or in the closed review dialog is lost; focus
  // in any other open dialog (say, a conflict raised by the activation) is
  // where it belongs, and nothing behind that dialog should take it.
  const focusPrimaryActionIfLost = () => {
    const focused = document.activeElement
    const lost =
      !focused ||
      focused === document.body ||
      !!focused.closest(`.${REVIEW_DIALOG_CLASS}`)
    const otherDialogOpen = Array.from(
      document.querySelectorAll<HTMLElement>(".ant-modal-wrap")
    ).some(
      (wrap) =>
        !wrap.classList.contains(REVIEW_DIALOG_CLASS) &&
        wrap.style.display !== "none"
    )
    if (!lost || otherDialogOpen) return
    document
      .querySelector<HTMLElement>(
        "[data-testid='automation-header-actions'] .ant-btn-primary"
      )
      ?.focus()
  }

  const initialValues = useMemo(
    () => (options ? defaultFormValues(options) : null),
    [options]
  )

  const setEditingRecord = (record: Automation | null) => {
    editingRef.current = record
    setEditing(record)
  }

  // antd does not report setFieldsValue through onValuesChange, so the review
  // panel's copy of the values is set here as well.
  const loadIntoForm = useCallback(
    (record: Automation | null, loadedOptions: AutomationOptions) => {
      const next = record
        ? recordToFormValues(record)
        : defaultFormValues(loadedOptions)
      form.resetFields()
      form.setFieldsValue(next)
      setValues(next)
      editingRef.current = record
      setEditing(record)
      dirtyRef.current = false
      setSaveWarnings([])
      setActivationBlocked([])
    },
    [form]
  )

  const loadOptions = useCallback(async () => {
    setLoadFailed(false)
    try {
      const response = await getAutomationOptions()
      if (response.ok && response.data) setOptions(response.data)
      else setLoadFailed(true)
    } catch {
      setLoadFailed(true)
    }
  }, [])

  // Opens the latest saved version, never a list row, which may be stale.
  const loadRecord = useCallback(
    async (recordId: string, loadedOptions: AutomationOptions) => {
      requestedIdRef.current = recordId
      setLoadFailed(false)
      setValues(null)
      let response
      try {
        response = await getAutomation(recordId)
      } catch {
        response = null
      }
      if (requestedIdRef.current !== recordId) return
      if (!response?.ok || !response.data) {
        setLoadFailed(true)
        return
      }
      loadIntoForm(response.data, loadedOptions)
    },
    [loadIntoForm]
  )

  useEffect(() => {
    void loadOptions()
  }, [loadOptions])

  useEffect(() => {
    if (!options) return
    // Saving a new automation moves the address to it; the form already holds it.
    if (id && editingRef.current?.id === id) return
    if (id) {
      void loadRecord(id, options)
    } else {
      requestedIdRef.current = undefined
      loadIntoForm(null, options)
    }
  }, [id, options, loadRecord, loadIntoForm])

  const retry = () => {
    if (!options) void loadOptions()
    else if (id) void loadRecord(id, options)
  }

  // Says an outcome out loud as well as in the toast, which screen readers
  // are not told about. A new id re-announces a repeated message.
  const report = (type: "success" | "error", text: string) => {
    void message[type](text)
    setAnnouncement((previous) => ({ id: previous.id + 1, text }))
  }

  // One request at a time; a second click or Enter while one runs (or while
  // the form is still being checked before it) is ignored.
  const runExclusive = async (
    action: BuilderAction,
    work: () => Promise<void>,
    { validateFirst = false } = {}
  ) => {
    if (busyRef.current) return
    // The lock is taken at once; the spinner and "Saving…" wait until the
    // form's rules pass, since checking them is not a request.
    busyRef.current = action
    try {
      if (validateFirst && !(await validateBuilder(form, focusBody))) return
      setBusy(action)
      await work()
    } finally {
      busyRef.current = null
      setBusy(null)
    }
  }

  // `toastFields` go in the toast even when their field is on screen: a copy's
  // name clash is not something the (valid) name field got wrong.
  const showServerErrors = (
    data: unknown,
    fallback: string,
    toastFields: (keyof AutomationFormValues)[] = []
  ) => {
    const details = readErrorDetails(data)
    const values = form.getFieldsValue(true) as AutomationFormValues
    const fieldNames = Object.keys(values) as (keyof AutomationFormValues)[]
    const allFields = fieldNames.flatMap((name) => {
      const errors = [name, ...(WIRE_TIMING_FIELDS[name] ?? [])].flatMap(
        (key) => {
          const raw = details[key]
          return Array.isArray(raw)
            ? raw.filter((item): item is string => typeof item === "string")
            : []
        }
      )
      if (
        !errors.length &&
        name === "channel" &&
        details.error_codes?.channel?.includes("unsupported_channel")
      ) {
        errors.push(t("settingsAutomation.builder.smsUnsupported"))
      }
      return errors.length ? [{ name, errors }] : []
    })
    // An error on a field that is not on screen would show nowhere.
    const onField = (name: keyof AutomationFormValues) =>
      isFieldShown(name, values) && !toastFields.includes(name)
    const fields = allFields.filter(({ name }) => onField(name))
    const hiddenErrors = allFields
      .filter(({ name }) => !onField(name))
      .flatMap(({ errors }) => errors)
    if (fields.length) {
      form.setFields(fields)
      focusFirstInvalid(
        form,
        fields.map(({ name }) => ({ name: [name] })),
        focusBody
      )
    }
    if (hiddenErrors.length) report("error", hiddenErrors.join(" "))
    else if (!fields.length) report("error", details.message || fallback)
  }

  const showConflict = (latest: Automation) => {
    modal.confirm({
      centered: true,
      title: t("settingsAutomation.builder.conflictTitle"),
      content: t("settingsAutomation.builder.conflictBody"),
      okText: t("settingsAutomation.builder.reload"),
      cancelText: t("settingsAutomation.builder.cancel"),
      onOk: () => {
        if (options) loadIntoForm(latest, options)
      }
    })
  }

  const createRecord = async (
    payload: AutomationPayload,
    toastFields: (keyof AutomationFormValues)[] = []
  ): Promise<Automation | null> => {
    let response
    try {
      response = await createAutomation(payload)
    } catch {
      report("error", saveFailed)
      return null
    }
    if (response.ok) return unwrapEnvelope<Automation>(response.data)
    showServerErrors(response.data, saveFailed, toastFields)
    return null
  }

  // Creates the draft, or PUTs the open record with its revision.
  const persist = async (): Promise<Automation | null> => {
    const payload = formValuesToPayload(
      form.getFieldsValue(true) as AutomationFormValues
    )
    const open = editingRef.current
    if (!open) return createRecord(payload)
    let response
    try {
      response = await updateAutomation(open.id, {
        ...payload,
        revision: open.revision
      })
    } catch {
      report("error", saveFailed)
      return null
    }
    if (response.ok) return unwrapEnvelope<Automation>(response.data)
    if (response.status === 409 && isConflict(response.data))
      showConflict(response.data.latest)
    else showServerErrors(response.data, saveFailed)
    return null
  }

  const afterSave = (saved: Automation, successText: string | null) => {
    if (!options) return
    loadIntoForm(withoutSaveExtras(saved), options)
    setSaveWarnings(saved.disabled_reasons ?? [])
    if (successText) report("success", successText)
    if (saved.id !== id && mountedRef.current) {
      ownNavigationRef.current = true
      try {
        navigate(RoutesPaths.settings.automationEdit(saved.id), {
          replace: true
        })
      } finally {
        ownNavigationRef.current = false
      }
    }
  }

  // An enable/disable bumps the revision by exactly one. A bigger jump means
  // someone else saved in between: adopting that revision would let the next
  // save overwrite their content without a conflict.
  const reconcileOpenRecord = (returned: Automation) => {
    const record = withoutSaveExtras(returned)
    const open = editingRef.current
    if (open?.id !== record.id) return
    if (record.revision === open.revision + 1) {
      setEditingRecord(record)
    } else if (!dirtyRef.current) {
      if (options) loadIntoForm(record, options)
    } else {
      setEditingRecord({
        ...open,
        is_enabled: record.is_enabled,
        enabled_at: record.enabled_at
      })
    }
  }

  const saveWork = async () => {
    const wasNew = !editingRef.current
    const saved = await persist()
    if (saved)
      afterSave(
        saved,
        t(`settingsAutomation.builder.${wasNew ? "created" : "saved"}`)
      )
  }

  // Switching an active Lead automation to Nurturing emails every lead in
  // Nurturing once saved, so that save goes through the review dialog first.
  const saveStartsNurturing = () => {
    const open = editingRef.current
    return (
      !!open &&
      scheduleWarning(
        open,
        form.getFieldsValue(true) as AutomationFormValues
      ) === "startNurturing"
    )
  }

  // Enter submits through the form, which has already run its rules.
  const save = () =>
    saveStartsNurturing()
      ? openReview("startNurturing")
      : runExclusive("save", saveWork)

  const saveFromHeader = () =>
    saveStartsNurturing()
      ? openReview("startNurturing")
      : runExclusive("save", saveWork, { validateFirst: true })

  // Holds the lock while it checks the form, but shows no spinner: opening
  // the dialog is not a request.
  const openReview = async (mode: ReviewMode = "activate") => {
    if (busyRef.current) return
    busyRef.current = mode === "activate" ? "activate" : "save"
    try {
      if (await validateBuilder(form, focusBody)) {
        setReviewMode(mode)
        setReviewOpen(true)
      }
    } finally {
      busyRef.current = null
    }
  }

  const confirmStartNurturing = () =>
    runExclusive("save", async () => {
      await saveWork()
      setReviewOpen(false)
    })

  const activate = () =>
    runExclusive("activate", async () => {
      const saved = await persist()
      if (!saved) {
        setReviewOpen(false)
        return
      }
      afterSave(saved, null)
      // Activation failing leaves the record as it was saved: a draft, or paused.
      const context = listStatus(saved) === "paused" ? "paused" : undefined
      let response
      try {
        response = await enableAutomation(saved.id)
      } catch {
        response = null
      }
      setReviewOpen(false)
      if (response?.ok) {
        refocusOnSwapRef.current = true
        reconcileOpenRecord(unwrapEnvelope<Automation>(response.data))
        report("success", t("settingsAutomation.builder.activated"))
        return
      }
      const body = response?.data
      if (isEnableBlocked(body)) {
        setActivationBlocked(body.reasons)
        setActivationBlockedPaused(context === "paused")
        setAnnouncement((previous) => ({
          id: previous.id + 1,
          text: t("settingsAutomation.builder.activationBlockedAnnouncement", {
            reasons: body.reasons.join(" "),
            context
          })
        }))
        return
      }
      report(
        "error",
        readErrorDetails(body).message ||
          t("settingsAutomation.builder.activateFailed", { context })
      )
    })

  const pause = () =>
    runExclusive("pause", async () => {
      const open = editingRef.current
      if (!open) return
      let response
      try {
        response = await disableAutomation(open.id)
      } catch {
        response = null
      }
      if (!response?.ok) {
        report(
          "error",
          readErrorDetails(response?.data).message ||
            t("settingsAutomation.builder.pauseFailed")
        )
        return
      }
      refocusOnSwapRef.current = true
      reconcileOpenRecord(unwrapEnvelope<Automation>(response.data))
      report("success", t("settingsAutomation.builder.paused"))
    })

  // A clean copy is the server's own duplicate, which numbers it "(copy N)"
  // against every name in the team and shortens it to fit.
  const duplicateOnServer = async (
    open: Automation
  ): Promise<Automation | null> => {
    let response
    try {
      response = await duplicateAutomation(open.id)
    } catch {
      response = null
    }
    if (response?.ok) return unwrapEnvelope<Automation>(response.data)
    report(
      "error",
      readErrorDetails(response?.data).message ||
        t("settingsAutomation.rowActions.duplicateFailed")
    )
    return null
  }

  const knownNames = async (): Promise<string[]> => {
    try {
      const response = await getAutomations({
        page: 1,
        page_size: NAMES_PAGE_SIZE
      })
      return response.ok
        ? (response.data?.results ?? []).map((row) => row.name)
        : []
    } catch {
      return []
    }
  }

  // Unsaved edits go into the copy, so it is created from the form, named the
  // way the server would name it. A name the server still rejects (a list it
  // could not be checked against) is reported in a toast.
  const duplicateWithEdits = async (
    open: Automation
  ): Promise<Automation | null> => {
    const payload = formValuesToPayload(
      form.getFieldsValue(true) as AutomationFormValues
    )
    const taken = [open.name, ...(await knownNames())]
    const takenLower = new Set(taken.map((name) => name.toLowerCase()))
    const renamed =
      payload.name.toLowerCase() !== open.name.trim().toLowerCase()
    const name =
      renamed && !takenLower.has(payload.name.toLowerCase())
        ? payload.name
        : copyName(payload.name, taken)
    return createRecord({ ...payload, name }, ["name"])
  }

  const duplicate = () =>
    runExclusive(
      "duplicate",
      async () => {
        const open = editingRef.current
        if (!open) return
        const copy = dirtyRef.current
          ? await duplicateWithEdits(open)
          : await duplicateOnServer(open)
        if (copy) afterSave(copy, t("settingsAutomation.builder.savedCopy"))
      },
      // Only a copy made from the form needs the form to be valid.
      { validateFirst: dirtyRef.current }
    )

  // A failed save behind the dialog set field errors; the dialog hands focus
  // back to its trigger as it closes, so move it to the first error after that.
  // An activation swaps the header's buttons, so that trigger may be gone.
  const focusAfterReview = () => {
    const errorFields = form
      .getFieldsError()
      .filter((field) => field.errors.length)
    if (errorFields.length) focusFirstInvalid(form, errorFields, focusBody)
    // After the dialog's own attempt to restore focus, which lands on nothing.
    else window.setTimeout(focusPrimaryActionIfLost)
  }

  const ready = !loadFailed && !!options && !!values && !!initialValues
  const status = editing ? listStatus(editing) : "draft"
  // What this save would do to a Nurturing schedule; text-only edits say nothing.
  const warnSchedule =
    editing && values ? scheduleWarning(editing, values) : null

  // Pause and activation swap the header's buttons, taking the focused one
  // with them; focus goes to the new primary action. (The review dialog's
  // afterClose covers the case where it hands focus to its vanished trigger.)
  // Waits for the request to finish: until then the new buttons are disabled.
  useEffect(() => {
    if (!refocusOnSwapRef.current || busy) return
    refocusOnSwapRef.current = false
    focusPrimaryActionIfLost()
  }, [status, busy])

  const renderBody = () => {
    if (loadFailed) {
      return (
        <Alert
          type={"error"}
          showIcon
          message={t("settingsAutomation.builder.loadErrorTitle")}
          description={t("settingsAutomation.builder.loadErrorBody")}
          action={
            <Button icon={<ReloadOutlined />} size={"small"} onClick={retry}>
              {t("settingsAutomation.builder.retry")}
            </Button>
          }
        />
      )
    }
    if (!ready) {
      return (
        <div
          role={"status"}
          aria-label={t("settingsAutomation.builder.loading")}
        >
          <Skeleton active />
        </div>
      )
    }
    return (
      <div
        className={
          "grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_clamp(440px,38.2vw,550px)]"
        }
      >
        <div className={"flex min-w-0 flex-col gap-4"}>
          {saveWarnings.length > 0 && (
            <Alert
              type={"warning"}
              showIcon
              closable
              onClose={() => setSaveWarnings([])}
              message={t("settingsAutomation.builder.saveWarningsTitle")}
              description={<ReasonList reasons={saveWarnings} />}
            />
          )}
          {activationBlocked.length > 0 && (
            <Alert
              type={"error"}
              showIcon
              closable
              onClose={() => setActivationBlocked([])}
              message={t("settingsAutomation.builder.activationBlockedTitle", {
                context: activationBlockedPaused ? "paused" : undefined
              })}
              description={<ReasonList reasons={activationBlocked} />}
            />
          )}
          <AutomationBuilder
            options={options}
            form={form}
            initialValues={initialValues}
            values={values}
            onValuesChange={(next) => {
              setValues(next)
              dirtyRef.current = true
            }}
            onSubmit={() => void save()}
            editorRef={editorRef}
            scheduleWarning={warnSchedule}
          />
        </div>
        <ReviewPanel
          values={values}
          options={options}
          status={status}
          busy={busy}
          onFocusField={(field) => focusField(form, field, focusBody)}
        />
      </div>
    )
  }

  const title: string =
    editing?.name ||
    (id
      ? t("settingsAutomation.builder.editTitle")
      : t("settingsAutomation.builder.newTitle"))

  return (
    <AutomationSurface>
      <div
        data-testid={"automation-builder"}
        className={"flex w-full min-w-0 flex-col gap-4"}
      >
        <BuilderHeader
          title={title}
          subtitle={
            editing ? undefined : t("settingsAutomation.builder.subtitle")
          }
          status={status}
          ready={ready}
          canDuplicate={!!editing}
          busy={busy}
          onSave={() => void saveFromHeader()}
          onActivate={() => void openReview()}
          onPause={() => void pause()}
          onDuplicate={() => void duplicate()}
          onPreview={() => scrollToAndFocus(LIVE_REVIEW_ID)}
        />
        {renderBody()}
        {/* The page's one announcement channel for save, activate, pause and
            duplicate outcomes; outside the review panel's aria-busy. */}
        <div
          role={"status"}
          aria-live={"polite"}
          className={"sr-only"}
          data-testid={"automation-announcer"}
        >
          <span key={announcement.id}>{announcement.text}</span>
        </div>
        {ready && (
          <ActivationDialog
            open={reviewOpen}
            sentence={summarizeFormValues(t, values, options).sentence}
            steps={journeySteps(t, values, options)}
            mode={reviewMode}
            note={
              reviewMode === "startNurturing"
                ? scheduleWarningText(t, "startNurturing", values)
                : undefined
            }
            activating={
              busy === (reviewMode === "activate" ? "activate" : "save")
            }
            onConfirm={() =>
              void (reviewMode === "activate"
                ? activate()
                : confirmStartNurturing())
            }
            onCancel={() => setReviewOpen(false)}
            afterClose={focusAfterReview}
          />
        )}
      </div>
    </AutomationSurface>
  )
}
